import type { SelectionChangedEvent } from 'ag-grid-community';
import { useAtom } from 'jotai';
import { useCallback } from 'react';

import { isReadOnlyManagedSourceType } from '@/features/SourceCreate/SourceCreate.constants';
import { selectedCloudSourceRowsAtom } from '@/pages/Administration/Devices/Devices.store';

import type { CloudSourceTreeNode } from '../CloudSourcesTable.types';

/**
 * Custom hook for managing the AG Grid instance for cloud sources table.
 *
 * Filters selected rows to only include sources (not provider groups) that have
 * activation permission.
 *
 * @returns Object containing:
 * - `onSelectionChanged` - Selection change callback that updates selected rows atom
 *
 * @example
 * ```tsx
 * const grid = useCloudSourcesGrid();
 * <AgGridReact
 *   onSelectionChanged={grid.onSelectionChanged}
 * />
 * ```
 */
export const useCloudSourcesGrid = () => {
  const [, setSelectedCloudSourceRows] = useAtom(selectedCloudSourceRowsAtom);

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<CloudSourceTreeNode>) => {
      const selectedRows = event.api.getSelectedRows();
      const allowedSelectedRows = selectedRows.filter(
        (row: CloudSourceTreeNode) =>
          row.type === 'source' &&
          row.allowSourceActivation &&
          !isReadOnlyManagedSourceType(row.sourceData?.source_type_id)
      );

      if (allowedSelectedRows.length !== selectedRows.length) {
        event.api.forEachNode((node) => {
          if (
            node.data?.type === 'source' &&
            isReadOnlyManagedSourceType(node.data?.sourceData?.source_type_id)
          ) {
            node.setSelected(false);
          }
        });
      }

      setSelectedCloudSourceRows(allowedSelectedRows);
    },
    [setSelectedCloudSourceRows]
  );

  return {
    onSelectionChanged
  };
};
